import {
  urlWithSearchParams,
  urlWithoutParams,
  parseRelativeUrl,
} from '../../common/utils/url';

/**
 * Returns the option by a product search is sorted. If the default rule (`MW`)
 * is used then null will be returned, because is not an option.
 * default one (Middleware)
 * @param {ProductSearch} productSearch - productSearch
 * @returns {SortingOption|null} - applied sorting option
 */
function getAppliedSortingOption(productSearch) {
  let result = null;
  const ruleId = productSearch?.productSort?.ruleId ?? null;
  if (ruleId !== null) {
    const options = productSearch?.productSort?.options ?? [];
    result = options.find((opt) => opt.sortingRuleID === ruleId);
  }
  return result ?? null;
}

/**
 * Returns the last page number
 * @returns {number} - last page number
 */
function getLastPageNumber({ count = 0, start = 0, pageSize = 12 }) {
  const lastItemOrdinal = count > 0 ? count - 1 : 0; // zero-based: 0, 1, 2...
  // miss-align start
  const misAlingStart = start % pageSize;
  if (misAlingStart && misAlingStart < count % pageSize) {
    return Math.ceil(lastItemOrdinal / pageSize);
  }
  return Math.floor(lastItemOrdinal / pageSize);
}

/**
 * Returns the page url for a given page number
 * @param {Object} productSearch - page number
 * @param {string} productSearch.permalink - page number
 * @param {number} productSearch.count - page number
 * @param {number} productSearch.pageNumber - page number
 * @param {number} productSearch.pageSize - page number
 * @param {number} productSearch.start - page number
 * @param {number} pageNumber - page number
 * @returns {string|null} - url
 */
function getPageUrl(
  {
    permalink,
    count,
    pageNumber: currentPageNumber,
    pageSize: currentPageSize,
    start: currentStart,
  },
  pageNumber
) {
  let start = 0;
  let sz = currentPageSize || 12;
  if (pageNumber === currentPageNumber) return null;
  if (pageNumber < currentPageNumber) {
    start = currentStart - sz;
    if (currentStart < currentPageSize) {
      sz = currentStart % currentPageSize;
      start = 0;
    }
  }
  if (pageNumber > currentPageNumber) {
    start = currentStart + sz;
    if (start < sz) {
      sz = start % count;
    }
  }
  return urlWithSearchParams(permalink, {
    start,
    sz,
  });
}

/**
 * Get the URL in order to apply some filter to a productSearch
 * @param {ProductSearch} productSearch - object with search results
 * @param {ProductSearchRefinementValue[]} selectedFilters - params for filters
 * @param {SortingOption} sortingOption - params for sorting
 * @returns {string} - the URL with necessary params
 */
function getUrlForFilters(
  productSearch,
  selectedFilters = [],
  sortingOption = null
) {
  const urlParams = {};
  // ATTRIBUTE FILTERS
  const attributeFiltersGroups = selectedFilters.reduce((res, item) => {
    if (!isRefinementValueForAttribute(item)) {
      return res; // go next
    }
    const prefn = item.id;
    const newGroup = !res[prefn];
    if (newGroup) {
      res[prefn] = [];
    }
    res[prefn].push(item.value);
    return res;
  }, {});
  let i = 0;
  Object.keys(attributeFiltersGroups).forEach((prefn) => {
    const values = attributeFiltersGroups[prefn];
    if (values?.length > 0) {
      i += 1;
      urlParams[`prefn${i}`] = prefn;
      urlParams[`prefv${i}`] = values.join('|');
    }
  });
  // PRICE FILTER
  const priceFilter = selectedFilters.find(
    (filter) => 'valueFrom' in filter && 'valueTo' in filter
  );
  if (priceFilter) {
    urlParams.pmin = priceFilter.valueFrom;
    urlParams.pmax = priceFilter.valueTo;
  }
  // SORTING
  if (sortingOption && sortingOption.sortingRuleID) {
    urlParams.srule = sortingOption.sortingRuleID;
  }
  let result = urlWithoutParams(productSearch.permalink);
  result = urlWithSearchParams(result, urlParams);
  result = parseRelativeUrl(result);
  return result;
}

/**
 * Returns true if the given productSearch is sorted by a diferent rule than the
 * default one (Middleware)
 * @param {ProductSearch} productSearch - productSearch
 * @returns {boolean} - has sorting applied
 */
function hasAnySortingOptionApplied(productSearch) {
  return getAppliedSortingOption(productSearch) !== null;
}

/**
 * Returns true if the given page number is the last of the category
 * @param {ProductSearch} productSearch - productSearch
 * @returns {boolean} - true if it is the last page
 */
function isLastPage(productSearch) {
  return (
    productSearch &&
    productSearch.pageNumber === getLastPageNumber(productSearch)
  );
}

/**
 * Check if a selected filter value is applied to an attribute
 * @param {ProductSearchRefinementValue} value - selected filter value
 * @returns {boolean} true or false
 */
function isRefinementValueForAttribute(value) {
  return !isRefinementValueForPrice(value);
}

/**
 * Check if a selected filter value is applied to price
 * @param {ProductSearchRefinementValue} value - selected filter value
 * @returns {boolean} true or false
 */
function isRefinementValueForPrice(value) {
  return 'valueFrom' in value && 'valueTo' in value;
}

export {
  getAppliedSortingOption,
  getPageUrl,
  getUrlForFilters,
  hasAnySortingOptionApplied,
  isLastPage,
  getLastPageNumber,
  isRefinementValueForAttribute,
  isRefinementValueForPrice,
};
